import axios, { type AxiosRequestConfig } from 'axios';
import { useSessionStore } from '@/stores/session';
import { i18n } from '@/i18n';

// The box API: https://bec.os-demo.tech/api/v1 in production. The company is the box's own — no header needed.
export const api = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL, timeout: 20000 });

api.interceptors.request.use((config) => {
  const session = useSessionStore();
  if (session.token) config.headers.Authorization = `Bearer ${session.token}`;
  config.headers['Accept-Language'] = localStorage.getItem('emcity_guest_lang') || 'en';
  return config;
});

// The access token is short-lived: on 401 get a new pair once and repeat the request; a dead refresh token
// (or none) ends the session.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (error.response?.status === 401 && config && !config._retried && !config.url?.includes('/customer/auth/')) {
      config._retried = true;
      if (await useSessionStore().renew()) return api(config);
    }
    if (error.response?.status === 401) useSessionStore().expire();
    return Promise.reject(error);
  },
);

export interface ApiErrorInfo {
  message: string;
  code?: string;
  data?: Record<string, unknown>;
}

/** {"detail", "code"}; field errors — 422 {"detail": [{"field", "message"}], "code": "validation_error"}. */
export function apiError(e: unknown, fallback: string): ApiErrorInfo {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const code = record.code as string | undefined;
    // Backend texts are English; known codes get the app's translation.
    const key = `errors.${code}`;
    if (code && i18n.global.te(key)) return { message: i18n.global.t(key), code, data: record };
    if (typeof record.detail === 'string') return { message: record.detail, code, data: record };
    if (Array.isArray(record.detail) && record.detail.length) {
      const first = record.detail[0] as { field?: string; message?: string };
      const fieldKey = `errors.fields.${first.field}`;
      const message = first.field && i18n.global.te(fieldKey) ? i18n.global.t(fieldKey) : first.message ?? fallback;
      return { message, code, data: record };
    }
  }
  return { message: fallback };
}
